/**
 * EventHubHomePage — the Event Hub landing surface.
 *
 * Route: `/event` (the "Event & Nomination" tab).
 *
 * The section list is role-dependent, following the Figma:
 *
 *   marketer   TaskOverview → Event Calendar
 *   BD         TaskOverview → Upcoming Events → Event Calendar
 *
 * "Upcoming Events" is BD-only on purpose: what a BD can nominate to, then what is coming up
 * that takes no nominations, over the next month (see upcomingEvents for the order). It is
 * not a thing a marketer acts on; a marketer's entry point is the create button on the
 * calendar. Its open-for-nomination events come from the same predicate as the "Open for
 * Nomination" count.
 *
 * The page owns ONE fetch of the event list (useEventList) and passes it down.
 * `ListEvents` has no filter parameters, so every section that needs events needs the
 * same whole set — fetching per section would be the same request two or three times
 * over.
 *
 * The calendar's VIEW and FILTERS live in this page's query string, not inside the
 * calendar. That is what lets the task counts at the top link into the list at the
 * bottom: `/event?view=table&status=DRAFT` switches to the table and selects Status =
 * Draft, so the page a user lands on shows the same N they clicked. There is no separate
 * event list page — the table view IS the list.
 *
 */

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Container,
  ContentLayout,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import {
  CreateEventModal,
  type CreateEventNext,
} from '../../components/CreateEventModal';
import EventHubTabs from '../../components/EventHubTabs/EventHubTabs';
import TaskOverview from '../../components/TaskOverview/TaskOverview';
import { EventCalendar, type CalendarView } from '../../components/EventCalendar';
import { NewEvents } from '../../components/NewEvents';
import { upcomingEvents } from '../../utils/upcomingEvents';
import {
  filtersFromParams,
  paramsFromFilters,
  type EventFilterState,
} from '../../components/EventFilters';
import {
  eventHubCalendarClient,
  type Event,
} from '../../client/EventCalendarClient';
import { useEventList } from '../../hooks/useEventList';
import { useEventHubRoles } from '../../hooks/useEventHubRoles';
import { useNominationList } from '../../hooks/useNominationList';
import { useNominationBusinessUnits } from '../../hooks/useNominationBusinessUnits';
import { useEventHubLogin } from '../../hooks/useEventHubLogin';
import { useEventHubLoading } from '../../components/EventHubLayout/EventHubLoading';
import {
  canManageEvents,
  canNominate,
  landingAudienceOf,
} from '../../utils/authorization';

/**
 * Which audience's landing surface an admin who is NEITHER a marketer nor a BD sees.
 *
 * Such an admin could act as both (the service lists ADMIN in every route's allowed roles),
 * but both at once is noise. An admin who also holds an audience role gets that audience's
 * page instead — see landingAudienceOf.
 *
 * To preview a real audience, act as one of its members (View as Other User): Event Hub then
 * renders from that user's roles, which the service reports (see useEventHubRoles).
 */
const ADMIN_VIEWS_AS: 'marketer' | 'bd' = 'bd';

/**
 * The query parameter that picks the calendar's view. Only `table` is spelled out; the
 * month grid is the default, so the tidiest URL is the commonest one.
 */
const VIEW_PARAM = 'view';

/**
 * Shared empty-state body. Kept as one component so the two sections read the same:
 * a short statement of what is absent, then what would put something here.
 */
function EmptyState({ headline, hint }: { headline: string; hint: string }) {
  return (
    <Box textAlign="center" padding={{ vertical: 'xl' }}>
      <Box variant="strong" color="text-body-secondary">
        {headline}
      </Box>
      <Box color="text-status-inactive" fontSize="body-s" padding={{ top: 'xxs' }}>
        {hint}
      </Box>
    </Box>
  );
}

export default function EventHubHomePage() {
  /* The login Event Hub acts as. Two things on this page are per-user — the owner a new event
     is created under, and which nominations count as "mine" — and both follow the viewed user
     so they agree with what the server attributes the requests to. */
  const { login } = useEventHubLogin();
  const navigate = useNavigate();
  const roles = useEventHubRoles();

  /**
   * Which audience's surface this is. Computed ONCE here and passed to every section,
   * so they cannot disagree — an earlier version had each section derive it, and an
   * admin ended up with the marketer task panel beside the BD-only New Events list.
   *
   * This is a VIEW decision, deliberately separate from the capability predicates:
   * `canManageEvents` answers "will the backend allow this", `viewAsMarketer` answers
   * "is this the marketer's page". They coincide for a real marketer and diverge for
   * an admin previewing the other side.
   */
  const { marketer: viewAsMarketer, bd: viewAsBd } = landingAudienceOf(roles, ADMIN_VIEWS_AS);

  const { events, loading, error } = useEventList();
  /* A second fetch, for the two "awaiting review" counts. Its failure is deliberately not
     surfaced: those tiles fall back to a dash, which is what they showed before the
     operation existed, and a banner about a count would push the whole page's real content
     down. The event list's failure IS surfaced, because everything below depends on it. */
  const { nominations, loading: loadingNominations } = useNominationList();
  /* The BUs an event must be for, for this caller to nominate to it: narrows the open count, the
     Upcoming Events cards and the calendar's "open" filter alike (utils/nominatableEvents). */
  const { businessUnits: bdBusinessUnits, loading: loadingBus } = useNominationBusinessUnits(
    canNominate(roles),
  );
  /* One overlay for the page's first load, rather than a dash in each tile, a spinner in New
     Events and a dimmed calendar. */
  useEventHubLoading(loading || loadingNominations || loadingBus);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  /* The URL is the single source of truth for the calendar's view and filters, so a
     deep link, the back button and the controls themselves all agree. */
  const view: CalendarView = searchParams.get(VIEW_PARAM) === 'table' ? 'table' : 'month';
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  /* `replace`, so typing in the search box does not leave one history entry per
     keystroke for the back button to walk through. */
  const writeParams = (next: URLSearchParams) =>
    setSearchParams(next, { replace: true });

  const setView = (next: CalendarView) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'table') params.set(VIEW_PARAM, next);
    else params.delete(VIEW_PARAM);
    writeParams(params);
  };

  const setFilters = (next: EventFilterState) =>
    // Written into a copy of the current parameters so the view survives a filter change.
    writeParams(paramsFromFilters(next, new URLSearchParams(searchParams)));

  const canCreate = canManageEvents(roles);

  /**
   * Where a freshly created event lands the user. Both exits go to the new event
   * rather than back to this page — it exists now, and a marketer who just named it
   * has nothing left to do here.
   */
  function onCreated(event: Event, next: CreateEventNext) {
    setCreateOpen(false);
    const path = `/event/events/${event.eventId}`;
    navigate(next === 'now' ? `${path}/edit` : path);
  }

  const upcoming = useMemo(
    () => (viewAsBd ? upcomingEvents(events, new Date(), bdBusinessUnits) : []),
    [events, viewAsBd, bdBusinessUnits],
  );

  return (
    <ContentLayout
      /* Tabs only — no page title. The Figma goes straight from the tab strip to the
         first card, and an "Event Hub" h1 under a tab strip that already says where you
         are would just be the same information twice. */
      header={<EventHubTabs />}
    >
      <SpaceBetween size="l">
        {/* Mounted only while open: an AWSUI Modal still renders its content when
            closed, and its 创建活动 header would collide with the page's own controls. */}
        {canCreate && createOpen ? (
          <CreateEventModal
            visible
            owner={login}
            onDismiss={() => setCreateOpen(false)}
            onSubmit={(input) => eventHubCalendarClient().createEvent(input)}
            onCreated={onCreated}
          />
        ) : null}

        {error ? (
          <Alert type="error" header="Failed to load events">
            {error}
          </Alert>
        ) : null}

        <TaskOverview
          viewAsMarketer={viewAsMarketer}
          viewAsBd={viewAsBd}
          events={events}
          nominations={nominations}
          alias={login}
          roles={roles}
          bdBusinessUnits={bdBusinessUnits}
          loading={loading || loadingNominations || loadingBus}
        />

        {viewAsBd ? (
          /* No card around the section: the Figma (1:9119) puts the heading and the event
             cards straight on the page background. See NewEvents.css. */
          <section className="eh-newevents-section" aria-label="Upcoming Events">
            <h2 className="eh-newevents-section__title">
              Upcoming Events{loading ? '' : ` (${upcoming.length})`}
            </h2>
            {loading ? null : upcoming.length === 0 ? (
              /* Distinct from "no events at all": the calendar below may be full while
                 nothing is coming up in the next month. */
              <EmptyState
                headline="No upcoming events"
                hint="Events open for nomination, and events in the next month, appear here."
              />
            ) : (
              <NewEvents
                events={upcoming}
                onViewDetail={(e) => navigate(`/event/events/${e.eventId}`)}
                onNominate={(e) =>
                  navigate(`/event/nominations/new?eventId=${e.eventId}`)
                }
              />
            )}
          </section>
        ) : null}

        <Container>
          <EventCalendar
            events={events}
            bdBusinessUnits={bdBusinessUnits}
            /* The BD's calendar only when the page is the BD's alone: someone who is also a
               marketer sees drafts, so Status and every BU still mean something to them. */
            bdView={viewAsBd && !viewAsMarketer}
            view={view}
            onViewChange={setView}
            filters={filters}
            onFiltersChange={setFilters}
            /* Exactly one primary action, chosen by the SAME view decision as the panels
               above — not by the capability predicates alone. An admin holds both
               capabilities, so gating on those put Create New Event and Nominate side by
               side, both primary, which is not a state any real user is in. This is the
               mistake the two task panels once made; the fix is the same one. Capability is
               still required on top, so a view never offers an action the service would
               refuse. */
            onCreateEvent={
              viewAsMarketer && canCreate ? () => setCreateOpen(true) : undefined
            }
            onNominate={
              viewAsBd && canNominate(roles)
                ? () => navigate('/event/nominations/new')
                : undefined
            }
            onSelectEvent={(e) => navigate(`/event/events/${e.eventId}`)}
          />
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
}
